import _ from 'lodash'

import { SalesOrderItem } from '@spa/services/api/orderHistory'
import { convertShortIdToDisplayId } from './id'
import Resource from './resource'

export class CommerceSalesOrder extends Resource {
  get compressedShortId(): string {
    return convertShortIdToDisplayId(this.shortId)
  }

  get salesOrderItems(): SalesOrderItem[] {
    return this.get('item', [])
  }

  get salesOrderStatus(): SalesOrderStatus {
    return this.get('sales_order_status')
  }

  /**
   * 注文リソースに紐づくカートIDのリストを返却する
   * item[].cart_idはオプショナルであるため空配列となることもある
   */
  get cartItemIds(): string[] {
    return _.compact(this.salesOrderItems.map(i => i.cart_id))
  }

  get totalPrice(): number {
    return this.get('totalPrice')
  }

  get chargeSheet(): ChargeSheet {
    return this.get('charge_sheet')
  }

  get delivery(): Delivery {
    return this.get('delivery')
  }

  get payment(): Payment {
    return this.get('payment')
  }

  get tax(): number {
    return this.get('tax')
  }

  /**
   * alias
   */
  get subtotalPrice(): number {
    return _.get(this.chargeSheet, 'order.priceIncludingTax.price', 0)
  }

  /**
   * alias
   */
  get commission(): number {
    return _.get(this.chargeSheet, 'payment.priceNoTax.price', 0)
  }

  get serviceFee(): number {
    return _.get(this.chargeSheet, 'service_fee.priceIncludingTax.price', 0)
  }

  /**
   * alias
   */
  get shipping(): number {
    return _.get(this.chargeSheet, 'delivery.priceIncludingTax.price', 0)
  }

  /**
   * alias
   * ポイント適用による割引額
   */
  get pointDiscountedPrice(): number {
    return _.get(this.payment, 'point.amount', 0)
  }

  /**
   * まとめて割引による割引額
   */
  get bundleDiscountPrice(): number {
    return this.get('aux_long_2', 0)
  }

  /**
   * クーポンによる割引額
   */
  get couponDiscountPrice(): number {
    return this.get('aux_long_3', 0)
  }

  /**
   * 付与（予定）ポイント
   */
  get estimatedPoints(): number {
    return this.get('aux_long_4', 0)
  }

  get cvsNumberResult(): CvsNumberResult | null {
    const authNumber = this.get('payment_provider_auth_number')
    const expireDate = this.get('payment_provider_expire_date')

    if (authNumber === undefined || expireDate === undefined) {
      return null
    }

    return { authNumber, expireDate }
  }

  get redirectHtml(): string | null {
    return this.get('payment.redirectHtml', null)
  }

  get supersedeStatus(): SupersedeStatus | undefined {
    return this.get('supersede_status', undefined)
  }

  get supersedingKind(): SupersedingKind | undefined {
    return this.get('superseding_kind', undefined)
  }
}

export interface ChargeSheet {
  delivery: PriceIncludingTax | PriceExcludingTax
  order: PriceIncludingTax
  payment: PriceNoTax
}

export interface Delivery {
  kind?: string
  date?: string
  timeSlot?: string
  timeSlotLabel?: string
  contact?: DeliveryContact
  contact_id?: string
}

export interface DeliveryContact {
  name?: DeliveryContactName
  address?: DeliveryContactAddress
  tel?: string
}

export interface DeliveryContactName {
  last_name?: string
  first_name?: string
}

export interface DeliveryContactAddress {
  /** 国 */
  country?: string
  /** 郵便番号 */
  code?: string
  /** 都道府県 */
  region?: string
  /** 市区町村 */
  locality?: string
  /** 番地 */
  street?: string
}

export interface Payment {
  method: PaymentMethod
  point: Point
}

export interface PaymentMethod {
  kind: string
}

export interface Point {
  amount: number
  kind: string
}

interface PriceExcludingTax {
  priceExcludingTax: EstimatePrice
}
interface PriceIncludingTax {
  priceIncludingTax: EstimatePrice
}
interface PriceNoTax {
  priceNoTax: EstimatePrice
}
interface EstimatePrice {
  price: number
  tax: number
}

/**
 * コンビニ決済時独自のフィールド
 */
interface CvsNumberResult {
  authNumber: string
  expireDate: string
}
/**
 * 配送準備保留・配送保留・配送不可について
 * @see https://github.com/my-color/front/issues/6932
 * @see https://my-color.esa.io/posts/628
 */
export type SalesOrderStatus =
  'payment-requested' |
  'payment-done' |
  'shipment-preparation' |
  'shipment-suspend-preparation' | // 配送準備保留
  'shipment-started' |
  'shipment-suspend-delivery' | // 配送保留
  'shipment-undeliverable' | // 配送不可
  'shipment-delivering' |
  'cancel' |
  'payment-error' |
  'payment-fatal-error'

export type SupersedeStatus = 'active' | 'superseded'
export type SupersedingKind = 'admin' | 'user'
